import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import {
  Plus, Search, Edit2, Trash2, Phone, Building2, Tag, ChevronRight, Mail, Save
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const Contacts = ({ contacts, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", company: "", position: "", address: "", notes: "", tags: []
  });

  const filteredContacts = contacts.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.company && c.company.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleOpenDialog = (contact = null) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({
        name: contact.name,
        email: contact.email,
        phone: contact.phone || "",
        company: contact.company || "",
        position: contact.position || "",
        address: contact.address || "",
        notes: contact.notes || "",
        tags: contact.tags || []
      });
    } else {
      setEditingContact(null);
      setFormData({ name: "", email: "", phone: "", company: "", position: "", address: "", notes: "", tags: [] });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingContact) {
        await axios.put(`${API}/contacts/${editingContact.id}`, formData);
      } else {
        await axios.post(`${API}/contacts`, formData);
      }
      setDialogOpen(false);
      onRefresh();
    } catch (error) {
      console.error("Error saving contact:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this contact?")) {
      try {
        await axios.delete(`${API}/contacts/${id}`);
        onRefresh();
      } catch (error) {
        console.error("Error deleting contact:", error);
      }
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contacts-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-cabinet text-3xl font-bold text-slate-900">Contacts</h2>
          <p className="text-slate-500 font-manrope">Manage your customer relationships</p>
        </div>
        <Button 
          data-testid="add-contact-btn"
          onClick={() => handleOpenDialog()} 
          className="bg-royal-blue-700 hover:bg-royal-blue-800"
        >
          <Plus size={18} className="mr-2" /> Add Contact
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
          <Input 
            data-testid="contact-search"
            placeholder="Search contacts..." 
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-4 font-medium text-slate-600">Name</th>
                  <th className="text-left p-4 font-medium text-slate-600">Email</th>
                  <th className="text-left p-4 font-medium text-slate-600">Company</th>
                  <th className="text-left p-4 font-medium text-slate-600">Phone</th>
                  <th className="text-left p-4 font-medium text-slate-600">Tags</th>
                  <th className="text-right p-4 font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredContacts.map((contact) => (
                  <tr key={contact.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-royal-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                          {contact.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{contact.name}</p>
                          <p className="text-sm text-slate-500">{contact.position}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-slate-600">{contact.email}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Building2 size={14} />
                        {contact.company || '-'}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Phone size={14} />
                        {contact.phone || '-'}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-wrap gap-1">
                        {contact.tags?.map((tag, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>
                        ))}
                      </div>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          data-testid={`edit-contact-${contact.id}`}
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleOpenDialog(contact)}
                        >
                          <Edit2 size={16} />
                        </Button>
                        <Button 
                          data-testid={`delete-contact-${contact.id}`}
                          variant="ghost" 
                          size="sm" 
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete(contact.id)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredContacts.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                No contacts found. Add your first contact!
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Contact Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-cabinet">{editingContact ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
            <DialogDescription>Fill in the contact details below</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input 
                  id="name" 
                  data-testid="contact-name-input"
                  value={formData.name} 
                  onChange={(e) => setFormData({...formData, name: e.target.value})} 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input 
                  id="email" 
                  type="email"
                  data-testid="contact-email-input"
                  value={formData.email} 
                  onChange={(e) => setFormData({...formData, email: e.target.value})} 
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input 
                  id="phone"
                  data-testid="contact-phone-input"
                  value={formData.phone} 
                  onChange={(e) => setFormData({...formData, phone: e.target.value})} 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Company</Label>
                <Input 
                  id="company"
                  data-testid="contact-company-input"
                  value={formData.company} 
                  onChange={(e) => setFormData({...formData, company: e.target.value})} 
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="position">Position</Label>
              <Input 
                id="position"
                data-testid="contact-position-input"
                value={formData.position} 
                onChange={(e) => setFormData({...formData, position: e.target.value})} 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea 
                id="notes"
                data-testid="contact-notes-input"
                value={formData.notes} 
                onChange={(e) => setFormData({...formData, notes: e.target.value})} 
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button 
              data-testid="save-contact-btn"
              onClick={handleSubmit} 
              className="bg-royal-blue-700 hover:bg-royal-blue-800"
            >
              <Save size={16} className="mr-2" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

